// CRAM list editor (Task 5, updated for the AML walk-through changes).
//
// What changed from the first iteration:
//  • Edits, adds, and deletes are now **buffered locally**. Nothing hits
//    Firestore until the user clicks "Save changes" at the bottom. This is
//    the staging ground for Task 6's preview-of-impact-on-customers flow
//    (we'll plug the diff into `cramPreviewRecalc` from here).
//  • Delete uses the shared <ConfirmationModal> instead of window.confirm,
//    and "deleted" rows stay visible (struck through) until Save.
//  • New "no_score" mode used by the Sectors list (sectors are reference
//    data only — no score column).
//  • Industries (industry_sectors) renders a Sector column with a
//    dropdown sourced from `cram_lists/sectors`.
//  • Column headers sort the table on click (incl. the FATF column).
//  • Search now matches FATF state too (typing "fatf", "yes", "true",
//    "no", "false" filters by FATF flag).
//  • Key column is narrowed; labels switch to <Textarea autosize> so long
//    Excel labels aren't truncated.
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    Divider,
    Group,
    Loader,
    NumberInput,
    ScrollArea,
    Select,
    Stack,
    Switch,
    Table,
    Text,
    Textarea,
    Title,
    Tooltip,
    UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    IconAlertCircle,
    IconArrowDown,
    IconArrowsSort,
    IconArrowUp,
    IconDeviceFloppy,
    IconInfoCircle,
    IconPlus,
    IconRotate,
    IconSearch,
    IconTrash,
} from "@tabler/icons-react";
import { collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useFirestore, useFunctions } from "../../../helpers/firebaseContext";
import { CramConfig, CramRecalcProposedItem } from "../../../types";
import {
    CramListMeta,
    itemRatingBadgeColor,
    itemRatingForScore,
    ratingBadgeColor,
    ratingForScore,
} from "../cramListsRegistry";
import CramAddItemModal, { DraftCramItem } from "./CramAddItemModal";
import { ConfirmationModal } from "../../../components/ConfirmationModal";
import CramRecalcPreviewModal from "./CramRecalcPreviewModal";

// Centralised row shape. Different lists use different fields; we keep them
// all optional and render conditionally based on list metadata.
export interface CramItemRow {
    id: string;
    key?: string;
    labelEn?: string;
    labelAr?: string;
    score?: number;
    scoreYes?: number;
    scoreNo?: number;
    flags?: Record<string, any>;
    // industries → reference into cram_lists/sectors:
    sectorKey?: string;
    sectorLabelEn?: string;
    sectorLabelAr?: string;
    // geography-only:
    label?: string;
    code2?: string;
    code3?: string;
    numeric?: string | number;
    [k: string]: any;
}

interface Props {
    listMeta: CramListMeta;
    config?: CramConfig;
    onItemCountChange?: (listId: string, count: number) => void;
    // The number we surface to the picker matches what AML sees in the
    // editor: server count + pendingAdds - pendingDeletes. We pass it up
    // from this component because the picker doesn't watch the draft.
}

const SCORE_MIN = 0;
const SCORE_MAX = 8;
const KEY_COL_WIDTH = 140;
const SCORE_COL_WIDTH = 110;

type SortField =
    | "id"
    | "labelEn"
    | "labelAr"
    | "score"
    | "rating"
    | "fatf"
    | "sector";

interface SortState {
    field: SortField;
    direction: "asc" | "desc";
}

interface DeleteTarget {
    id: string;
    label: string;
}

// Field-level equality helpers used by updateDraftField to detect "no-op"
// edits (user changes a value and then types it back to the original).
// When the draft and server match across every editable field, we drop
// the row from pendingUpdates so the dirty banner / Save button reflect
// reality.
function normalizeScore(s: number | undefined): number | null {
    return typeof s === "number" && Number.isFinite(s) ? s : null;
}

function rowEqualsServer(
    server: CramItemRow | undefined,
    draft: CramItemRow
): boolean {
    if (!server) return false;
    if ((server.labelEn ?? "") !== (draft.labelEn ?? "")) return false;
    if ((server.labelAr ?? "") !== (draft.labelAr ?? "")) return false;
    if (normalizeScore(server.score) !== normalizeScore(draft.score))
        return false;
    if ((server.sectorKey ?? "") !== (draft.sectorKey ?? "")) return false;
    if (!!server.flags?.fatfFlag !== !!draft.flags?.fatfFlag) return false;
    return true;
}

// ---------------------------------------------------------------
// Header cell (sortable). Declared at module scope (not inside the
// editor's render body) so its component identity stays stable across
// renders — otherwise React would unmount/remount every header cell on
// each render. `sort` + `onSort` are passed in as props.
// ---------------------------------------------------------------
const HeaderCell: React.FC<{
    field?: SortField;
    children: React.ReactNode;
    width?: number;
    sort: SortState;
    onSort: (field: SortField) => void;
}> = ({ field, children, width, sort, onSort }) => {
    const inner = (
        <Group gap={4} wrap="nowrap" justify="space-between">
            <Text size="xs" fw={600}>
                {children}
            </Text>
            {field &&
                (sort.field === field ? (
                    sort.direction === "asc" ? (
                        <IconArrowUp size={12} />
                    ) : (
                        <IconArrowDown size={12} />
                    )
                ) : (
                    <IconArrowsSort size={12} style={{ opacity: 0.4 }} />
                ))}
        </Group>
    );
    return (
        <Table.Th style={width ? { width } : undefined}>
            {field ? (
                <UnstyledButton
                    onClick={() => onSort(field)}
                    style={{ width: "100%" }}
                >
                    {inner}
                </UnstyledButton>
            ) : (
                inner
            )}
        </Table.Th>
    );
};

const CramListEditor: React.FC<Props> = ({
    listMeta,
    config,
    onItemCountChange,
}) => {
    const db = useFirestore();
    const functions = useFunctions();

    // `serverItems` mirrors Firestore. `draft` is what the user sees and
    // edits; it stays in sync with serverItems whenever there are no
    // pending local changes (i.e. on initial load and right after Save).
    const [serverItems, setServerItems] = useState<CramItemRow[]>([]);
    const [draft, setDraft] = useState<CramItemRow[]>([]);
    const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(
        new Set()
    );
    const [pendingAdds, setPendingAdds] = useState<Set<string>>(new Set());
    const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(
        new Set()
    );

    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(
        null
    );
    const [saving, setSaving] = useState(false);
    // Task 6: id of the in-flight preview/apply job. When set, the
    // CramRecalcPreviewModal is open and the buffered edits stay put until
    // the job is applied or discarded.
    const [previewJobId, setPreviewJobId] = useState<string | null>(null);
    const [sort, setSort] = useState<SortState>({
        field: "labelEn",
        direction: "asc",
    });

    // Sector reference data (only when listMeta.sectorRefListId is set —
    // currently just industry_sectors → sectors).
    const [sectorOptions, setSectorOptions] = useState<
        { value: string; label: string }[]
    >([]);

    // Hold the dirty state in a ref so the snapshot handler can decide
    // whether to overwrite the draft. We update the ref during render
    // (instead of inside an effect) so that snapshots arriving between
    // a setState call and the post-render effect can't briefly observe
    // dirtyRef.current === false and clobber the user's in-progress edit.
    const dirtyRef = useRef(false);
    dirtyRef.current =
        pendingUpdates.size > 0 ||
        pendingAdds.size > 0 ||
        pendingDeletes.size > 0;

    // Lookup by id for fast diffing against the server snapshot.
    const serverRowMap = useMemo(
        () => new Map(serverItems.map((r) => [r.id, r])),
        [serverItems]
    );

    // Main list subscription.
    useEffect(() => {
        setLoading(true);
        setServerItems([]);
        setDraft([]);
        setPendingUpdates(new Set());
        setPendingAdds(new Set());
        setPendingDeletes(new Set());
        setSearch("");
        const itemsRef = collection(db, "cram_lists", listMeta.id, "items");
        const unsub = onSnapshot(
            itemsRef,
            (snap) => {
                const next: CramItemRow[] = [];
                snap.forEach((d) => {
                    next.push({ id: d.id, ...(d.data() as any) });
                });
                setServerItems(next);
                setLoading(false);
                // Only adopt the server view as the draft when the user
                // has no buffered edits — otherwise we'd clobber their
                // work-in-progress.
                if (!dirtyRef.current) {
                    setDraft(next);
                }
            },
            (err) => {
                console.error("[CRAM] items snapshot error:", err);
                setLoading(false);
                notifications.show({
                    title: "Failed to load list",
                    message: err.message,
                    color: "red",
                    icon: <IconAlertCircle size={16} />,
                });
            }
        );
        return () => unsub();
    }, [db, listMeta.id]);

    // Sector reference subscription (for industries → sectors dropdown).
    useEffect(() => {
        if (!listMeta.sectorRefListId) {
            setSectorOptions([]);
            return;
        }
        const refColl = collection(
            db,
            "cram_lists",
            listMeta.sectorRefListId,
            "items"
        );
        const unsub = onSnapshot(refColl, (snap) => {
            const opts: { value: string; label: string }[] = [];
            snap.forEach((d) => {
                const data = d.data() as any;
                opts.push({
                    value: d.id,
                    label: (data.labelEn || data.label || d.id) as string,
                });
            });
            opts.sort((a, b) => a.label.localeCompare(b.label));
            setSectorOptions(opts);
        });
        return () => unsub();
    }, [db, listMeta.sectorRefListId]);

    // Surface "visible count" (server count + adds - deletes) to the
    // picker so AML sees what's actually in scope right now.
    useEffect(() => {
        const visibleCount =
            serverItems.length + pendingAdds.size - pendingDeletes.size;
        onItemCountChange?.(listMeta.id, visibleCount);
    }, [
        serverItems.length,
        pendingAdds,
        pendingDeletes,
        listMeta.id,
        onItemCountChange,
    ]);

    // Buffered field edit. Doesn't touch Firestore; just mutates the draft
    // and toggles the row's pending-update marker based on whether the
    // resulting row still differs from the server snapshot. This makes
    // "change and revert" a true no-op (Save button + counters reset).
    const updateDraftField = useCallback(
        (id: string, patch: Partial<CramItemRow>) => {
            const current = draft.find((r) => r.id === id);
            if (!current) return;
            const nextRow: CramItemRow = { ...current, ...patch };
            setDraft((prev) =>
                prev.map((r) => (r.id === id ? nextRow : r))
            );
            if (pendingAdds.has(id)) return;
            const dirty = !rowEqualsServer(serverRowMap.get(id), nextRow);
            setPendingUpdates((prev) => {
                const has = prev.has(id);
                if (dirty && !has) {
                    const next = new Set(prev);
                    next.add(id);
                    return next;
                }
                if (!dirty && has) {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                }
                return prev;
            });
        },
        [draft, pendingAdds, serverRowMap]
    );

    const handleDeleteRequest = useCallback((row: CramItemRow) => {
        const label = row.labelEn || row.label || row.id;
        setDeleteTarget({ id: row.id, label });
    }, []);

    const performDelete = useCallback(() => {
        if (!deleteTarget) return;
        const { id } = deleteTarget;
        if (pendingAdds.has(id)) {
            // Cancelling an unsaved add: just remove the draft row.
            setDraft((prev) => prev.filter((r) => r.id !== id));
            setPendingAdds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        } else {
            setPendingDeletes((prev) => {
                const next = new Set(prev);
                next.add(id);
                return next;
            });
            // If it was also marked as updated, drop the update marker —
            // delete wins.
            setPendingUpdates((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
        setDeleteTarget(null);
    }, [deleteTarget, pendingAdds]);

    const undoDelete = useCallback((id: string) => {
        setPendingDeletes((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const addNewItem = useCallback(
        (draftItem: DraftCramItem) => {
            // Refuse duplicate doc IDs.
            const exists =
                draft.some((r) => r.id === draftItem.id) ||
                serverItems.some((r) => r.id === draftItem.id);
            if (exists) {
                notifications.show({
                    title: "Duplicate key",
                    message: `An item with key "${draftItem.id}" already exists.`,
                    color: "red",
                    icon: <IconAlertCircle size={16} />,
                });
                return false;
            }
            const row: CramItemRow = {
                id: draftItem.id,
                key: draftItem.id,
                labelEn: draftItem.labelEn,
                labelAr: draftItem.labelAr,
                ...(listMeta.mode !== "no_score"
                    ? { score: draftItem.score ?? 0 }
                    : {}),
                ...(draftItem.sectorKey
                    ? { sectorKey: draftItem.sectorKey }
                    : {}),
                flags: {},
            };
            setDraft((prev) => [...prev, row]);
            setPendingAdds((prev) => {
                const next = new Set(prev);
                next.add(draftItem.id);
                return next;
            });
            return true;
        },
        [draft, serverItems, listMeta.mode]
    );

    const discardChanges = useCallback(() => {
        setDraft(serverItems);
        setPendingAdds(new Set());
        setPendingUpdates(new Set());
        setPendingDeletes(new Set());
    }, [serverItems]);

    // Task 6 wiring point: instead of writing the list straight to Firestore,
    // send the proposed rows (draft minus staged deletes) to cramPreviewRecalc.
    // That creates a cram_recalc_jobs/{jobId} doc; the CramRecalcPreviewModal
    // then shows the affected-customer diff and (on confirm) calls
    // cramApplyRecalc, which performs the actual list write + recompute. The
    // local buffer is preserved until the job is applied or discarded.
    const saveAllChanges = useCallback(async () => {
        // Don't start a second preview while one is already open — it would
        // create an orphan job from a possibly-stale draft.
        if (previewJobId) return;
        const hasChanges =
            pendingAdds.size > 0 ||
            pendingUpdates.size > 0 ||
            pendingDeletes.size > 0;
        if (!hasChanges) return;
        setSaving(true);
        try {
            const proposedItems: CramRecalcProposedItem[] = draft
                .filter((r) => !pendingDeletes.has(r.id))
                .map((r) => {
                    const item: CramRecalcProposedItem = {
                        id: r.id,
                        key: r.key ?? r.id,
                        labelEn: r.labelEn ?? r.label ?? "",
                        labelAr: r.labelAr ?? "",
                        flags: r.flags ?? {},
                    };
                    if (typeof r.score === "number") item.score = r.score;
                    if (typeof r.scoreYes === "number")
                        item.scoreYes = r.scoreYes;
                    if (typeof r.scoreNo === "number") item.scoreNo = r.scoreNo;
                    if (r.sectorKey) item.sectorKey = r.sectorKey;
                    return item;
                });

            const previewFn = httpsCallable<
                { listId: string; proposedItems: CramRecalcProposedItem[] },
                { jobId: string }
            >(functions, "cramPreviewRecalc");
            const res = await previewFn({ listId: listMeta.id, proposedItems });
            setPreviewJobId(res.data.jobId);
        } catch (err: any) {
            console.error("[CRAM] preview error:", err);
            notifications.show({
                title: "Could not start preview",
                message: err?.message ?? "Unknown error",
                color: "red",
                icon: <IconAlertCircle size={16} />,
            });
        } finally {
            setSaving(false);
        }
    }, [
        functions,
        draft,
        listMeta.id,
        pendingAdds,
        pendingDeletes,
        pendingUpdates,
        previewJobId,
    ]);

    // Called by the preview modal once the apply job reaches "applied": drop
    // the local buffer so the live snapshot (now the new source of truth)
    // becomes the draft on the next onSnapshot tick.
    const handleApplied = useCallback(() => {
        setPendingAdds(new Set());
        setPendingUpdates(new Set());
        setPendingDeletes(new Set());
    }, []);

    // ---------------------------------------------------------------
    // Filtering + sorting
    // ---------------------------------------------------------------
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return draft;
        // FATF search shortcuts: typing fatf/yes/no/true/false filters
        // by the flag. Composing those with the normal substring search
        // means typing "fatf" highlights only FATF=true rows.
        const fatfMatch =
            q === "fatf" ||
            q === "fatf yes" ||
            q === "fatf:yes" ||
            q === "yes" ||
            q === "true";
        const fatfNoMatch =
            q === "fatf no" ||
            q === "fatf:no" ||
            q === "no" ||
            q === "false";
        return draft.filter((it) => {
            const en = (it.labelEn || it.label || "").toString().toLowerCase();
            const ar = (it.labelAr || "").toString().toLowerCase();
            const k = (it.key || it.id || "").toString().toLowerCase();
            const sect = (it.sectorKey || it.sectorLabelEn || "")
                .toString()
                .toLowerCase();
            const fatfOn = !!it.flags?.fatfFlag;
            if (fatfMatch) return fatfOn;
            if (fatfNoMatch && listMeta.showFatfFlag) return !fatfOn;
            const hits =
                en.includes(q) ||
                ar.includes(q) ||
                k.includes(q) ||
                sect.includes(q);
            return hits;
        });
    }, [draft, search, listMeta.showFatfFlag]);

    const sortedFiltered = useMemo(() => {
        const rows = [...filtered];
        const dir = sort.direction === "asc" ? 1 : -1;
        const getter = (row: CramItemRow): string | number => {
            switch (sort.field) {
                case "id":
                    return row.id;
                case "labelEn":
                    return (row.labelEn || row.label || row.id).toString();
                case "labelAr":
                    return (row.labelAr || "").toString();
                case "score": {
                    const s = listMeta.isYesNo ? row.scoreYes : row.score;
                    return typeof s === "number" ? s : -Infinity;
                }
                case "rating": {
                    const s = listMeta.isYesNo ? row.scoreYes : row.score;
                    const r =
                        listMeta.id === "geography"
                            ? itemRatingForScore(s)
                            : ratingForScore(s, config?.buckets);
                    const rank: Record<string, number> = {
                        Low: 1,
                        Medium: 2,
                        High: 3,
                        "Very High": 4,
                        Prohibited: 5,
                    };
                    return r ? rank[r] : 0;
                }
                case "fatf":
                    return row.flags?.fatfFlag ? 1 : 0;
                case "sector":
                    return (
                        row.sectorLabelEn ||
                        row.sectorKey ||
                        ""
                    ).toString();
                default:
                    return "";
            }
        };
        rows.sort((a, b) => {
            const av = getter(a);
            const bv = getter(b);
            if (typeof av === "number" && typeof bv === "number") {
                return (av - bv) * dir;
            }
            return av.toString().localeCompare(bv.toString()) * dir;
        });
        return rows;
    }, [filtered, sort, listMeta.id, listMeta.isYesNo, config?.buckets]);

    const handleSort = useCallback(
        (field: SortField) => {
            setSort((prev) =>
                prev.field === field
                    ? {
                          field,
                          direction:
                              prev.direction === "asc" ? "desc" : "asc",
                      }
                    : { field, direction: "asc" }
            );
        },
        []
    );

    const isFull = listMeta.mode === "full";
    const isView = listMeta.mode === "view";
    const isScoreOnly = listMeta.mode === "score_only";
    const isNoScore = listMeta.mode === "no_score";
    const canAdd = isFull || isNoScore;
    const canDeleteRows = isFull || isNoScore;
    const showScore = !isNoScore;

    const pendingCount =
        pendingAdds.size + pendingUpdates.size + pendingDeletes.size;

    return (
        <Card withBorder radius="sm" p="md">
            <Group justify="space-between" mb="xs">
                <Box>
                    <Title order={5}>{listMeta.labelEn}</Title>
                    <Text size="xs" c="dimmed">
                        {listMeta.description}
                    </Text>
                </Box>
                <Group gap="xs">
                    <Textarea
                        size="xs"
                        placeholder="Search (try: fatf, yes, no)"
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        autosize
                        minRows={1}
                        maxRows={1}
                        leftSection={<IconSearch size={14} />}
                        w={260}
                    />
                    {canAdd && (
                        <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => setAddOpen(true)}
                        >
                            Add Row
                        </Button>
                    )}
                </Group>
            </Group>

            {isView && (
                <Alert
                    icon={<IconInfoCircle size={16} />}
                    color="gray"
                    variant="light"
                    mb="sm"
                >
                    This list is read-only here. Edits to{" "}
                    <Text span fw={600}>
                        {listMeta.labelEn}
                    </Text>{" "}
                    are managed via Firestore and backend code.
                </Alert>
            )}

            {isScoreOnly && (
                <Alert
                    icon={<IconInfoCircle size={16} />}
                    color="gray"
                    variant="light"
                    mb="sm"
                >
                    Rows in this list are managed automatically. You can update
                    the score
                    {listMeta.showFatfFlag ? " and FATF flag" : ""}. Click{" "}
                    <Text span fw={600}>
                        Save changes
                    </Text>{" "}
                    at the bottom to apply.
                </Alert>
            )}

            {pendingCount > 0 && (
                <Alert
                    icon={<IconInfoCircle size={16} />}
                    color="yellow"
                    variant="light"
                    mb="sm"
                >
                    You have <Text span fw={600}>{pendingCount}</Text>{" "}
                    unsaved change{pendingCount === 1 ? "" : "s"}. Nothing is
                    persisted yet — click <Text span fw={600}>Save changes</Text>{" "}
                    to apply or <Text span fw={600}>Discard</Text> to revert.
                </Alert>
            )}

            <Divider mb="xs" />

            {loading ? (
                <Group justify="center" py="lg">
                    <Loader size="sm" />
                </Group>
            ) : (
                <ScrollArea h={520}>
                    <Table
                        striped
                        highlightOnHover
                        withTableBorder
                        verticalSpacing="xs"
                        style={{ tableLayout: "fixed", width: "100%" }}
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <HeaderCell
                                    field="id"
                                    width={KEY_COL_WIDTH}
                                    sort={sort}
                                    onSort={handleSort}
                                >
                                    Key
                                </HeaderCell>
                                <HeaderCell
                                    field="labelEn"
                                    sort={sort}
                                    onSort={handleSort}
                                >
                                    Label (EN)
                                </HeaderCell>
                                {listMeta.showArabic && (
                                    <HeaderCell
                                        field="labelAr"
                                        sort={sort}
                                        onSort={handleSort}
                                    >
                                        Label (AR)
                                    </HeaderCell>
                                )}
                                {listMeta.sectorRefListId && (
                                    <HeaderCell
                                        field="sector"
                                        width={200}
                                        sort={sort}
                                        onSort={handleSort}
                                    >
                                        Sector
                                    </HeaderCell>
                                )}
                                {showScore && listMeta.isYesNo ? (
                                    <>
                                        <HeaderCell
                                            field="score"
                                            width={SCORE_COL_WIDTH}
                                            sort={sort}
                                            onSort={handleSort}
                                        >
                                            Score (Yes)
                                        </HeaderCell>
                                        <HeaderCell
                                            width={SCORE_COL_WIDTH}
                                            sort={sort}
                                            onSort={handleSort}
                                        >
                                            Score (No)
                                        </HeaderCell>
                                    </>
                                ) : showScore ? (
                                    <HeaderCell
                                        field="score"
                                        width={SCORE_COL_WIDTH}
                                        sort={sort}
                                        onSort={handleSort}
                                    >
                                        Score
                                    </HeaderCell>
                                ) : null}
                                {showScore && (
                                    <HeaderCell
                                        field="rating"
                                        width={110}
                                        sort={sort}
                                        onSort={handleSort}
                                    >
                                        Rating
                                    </HeaderCell>
                                )}
                                {listMeta.showFatfFlag && (
                                    <HeaderCell
                                        field="fatf"
                                        width={90}
                                        sort={sort}
                                        onSort={handleSort}
                                    >
                                        FATF
                                    </HeaderCell>
                                )}
                                {canDeleteRows && (
                                    <Table.Th style={{ width: 60 }}> </Table.Th>
                                )}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {sortedFiltered.length === 0 && (
                                <Table.Tr>
                                    <Table.Td
                                        colSpan={
                                            1 +
                                            1 +
                                            (listMeta.showArabic ? 1 : 0) +
                                            (listMeta.sectorRefListId ? 1 : 0) +
                                            (showScore
                                                ? listMeta.isYesNo
                                                    ? 3
                                                    : 2
                                                : 0) +
                                            (listMeta.showFatfFlag ? 1 : 0) +
                                            (canDeleteRows ? 1 : 0)
                                        }
                                    >
                                        <Text c="dimmed" ta="center" py="md">
                                            {search
                                                ? "No matching rows."
                                                : "No items in this list."}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {sortedFiltered.map((row) => {
                                const isAdd = pendingAdds.has(row.id);
                                const isUpdate = pendingUpdates.has(row.id);
                                const isDelete = pendingDeletes.has(row.id);
                                const labelEditable =
                                    isFull || isNoScore || isAdd;
                                const ratingScore = listMeta.isYesNo
                                    ? row.scoreYes
                                    : row.score;
                                const isGeography =
                                    listMeta.id === "geography";
                                let rating:
                                    | ReturnType<typeof itemRatingForScore>
                                    | ReturnType<typeof ratingForScore>;
                                let ratingColor: string;
                                if (isGeography) {
                                    const geoRating =
                                        itemRatingForScore(ratingScore);
                                    rating = geoRating;
                                    ratingColor =
                                        itemRatingBadgeColor(geoRating);
                                } else {
                                    const bucketRating = ratingForScore(
                                        ratingScore,
                                        config?.buckets
                                    );
                                    rating = bucketRating;
                                    ratingColor =
                                        ratingBadgeColor(bucketRating);
                                }
                                const labelEn =
                                    row.labelEn || row.label || row.id;
                                const rowStyle: React.CSSProperties = isDelete
                                    ? {
                                          opacity: 0.5,
                                          textDecoration: "line-through",
                                          background:
                                              "var(--mantine-color-red-0)",
                                      }
                                    : isAdd
                                    ? {
                                          background:
                                              "var(--mantine-color-green-0)",
                                      }
                                    : isUpdate
                                    ? {
                                          background:
                                              "var(--mantine-color-yellow-0)",
                                      }
                                    : {};
                                return (
                                    <Table.Tr key={row.id} style={rowStyle}>
                                        <Table.Td
                                            style={{
                                                width: KEY_COL_WIDTH,
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            <Tooltip
                                                label={row.id}
                                                openDelay={300}
                                            >
                                                <Text size="xs" c="dimmed" lineClamp={2}>
                                                    {row.id}
                                                </Text>
                                            </Tooltip>
                                        </Table.Td>
                                        <Table.Td>
                                            {labelEditable && !isDelete ? (
                                                <Stack gap={2}>
                                                    <Textarea
                                                        size="xs"
                                                        autosize
                                                        minRows={1}
                                                        maxRows={4}
                                                        value={
                                                            row.labelEn ?? ""
                                                        }
                                                        onChange={(e) =>
                                                            updateDraftField(
                                                                row.id,
                                                                {
                                                                    labelEn:
                                                                        e
                                                                            .currentTarget
                                                                            .value,
                                                                }
                                                            )
                                                        }
                                                    />
                                                    {isUpdate &&
                                                        (serverRowMap.get(
                                                            row.id
                                                        )?.labelEn ?? "") !==
                                                            (row.labelEn ??
                                                                "") && (
                                                            <Text
                                                                size="xs"
                                                                c="dimmed"
                                                            >
                                                                was:{" "}
                                                                {serverRowMap.get(
                                                                    row.id
                                                                )?.labelEn ||
                                                                    "—"}
                                                            </Text>
                                                        )}
                                                </Stack>
                                            ) : (
                                                <Text size="sm">{labelEn}</Text>
                                            )}
                                        </Table.Td>
                                        {listMeta.showArabic && (
                                            <Table.Td>
                                                {labelEditable && !isDelete ? (
                                                    <Stack gap={2}>
                                                        <Textarea
                                                            size="xs"
                                                            autosize
                                                            minRows={1}
                                                            maxRows={4}
                                                            dir="rtl"
                                                            value={
                                                                row.labelAr ??
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                updateDraftField(
                                                                    row.id,
                                                                    {
                                                                        labelAr:
                                                                            e
                                                                                .currentTarget
                                                                                .value,
                                                                    }
                                                                )
                                                            }
                                                        />
                                                        {isUpdate &&
                                                            (serverRowMap.get(
                                                                row.id
                                                            )?.labelAr ?? "") !==
                                                                (row.labelAr ??
                                                                    "") && (
                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                    dir="rtl"
                                                                >
                                                                    was:{" "}
                                                                    {serverRowMap.get(
                                                                        row.id
                                                                    )?.labelAr ||
                                                                        "—"}
                                                                </Text>
                                                            )}
                                                    </Stack>
                                                ) : (
                                                    <Text size="sm" dir="rtl">
                                                        {row.labelAr || "—"}
                                                    </Text>
                                                )}
                                            </Table.Td>
                                        )}
                                        {listMeta.sectorRefListId && (
                                            <Table.Td>
                                                {!isDelete &&
                                                (isFull || isAdd) ? (
                                                    <Stack gap={2}>
                                                        <Select
                                                            size="xs"
                                                            data={sectorOptions}
                                                            value={
                                                                row.sectorKey ??
                                                                null
                                                            }
                                                            searchable
                                                            clearable
                                                            placeholder="(none)"
                                                            onChange={(v) =>
                                                                updateDraftField(
                                                                    row.id,
                                                                    {
                                                                        sectorKey:
                                                                            v ??
                                                                            "",
                                                                    }
                                                                )
                                                            }
                                                        />
                                                        {isUpdate &&
                                                            (serverRowMap.get(
                                                                row.id
                                                            )?.sectorKey ??
                                                                "") !==
                                                                (row.sectorKey ??
                                                                    "") && (
                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    was:{" "}
                                                                    {sectorOptions.find(
                                                                        (o) =>
                                                                            o.value ===
                                                                            serverRowMap.get(
                                                                                row.id
                                                                            )
                                                                                ?.sectorKey
                                                                    )?.label ||
                                                                        serverRowMap.get(
                                                                            row.id
                                                                        )
                                                                            ?.sectorLabelEn ||
                                                                        "—"}
                                                                </Text>
                                                            )}
                                                    </Stack>
                                                ) : (
                                                    <Text size="sm">
                                                        {row.sectorLabelEn ||
                                                            sectorOptions.find(
                                                                (o) =>
                                                                    o.value ===
                                                                    row.sectorKey
                                                            )?.label ||
                                                            "—"}
                                                    </Text>
                                                )}
                                            </Table.Td>
                                        )}
                                        {showScore && listMeta.isYesNo ? (
                                            <>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {row.scoreYes ?? "—"}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {row.scoreNo ?? "—"}
                                                    </Text>
                                                </Table.Td>
                                            </>
                                        ) : showScore ? (
                                            <Table.Td>
                                                {isView || isDelete ? (
                                                    <Text size="sm">
                                                        {row.score ?? "—"}
                                                    </Text>
                                                ) : (
                                                    <Stack gap={2}>
                                                        <NumberInput
                                                            size="xs"
                                                            // Controlled by the draft. clamp=none stops Mantine
                                                            // from rewriting the field on blur/Tab (which used
                                                            // to silently reset empty fields back to min=0).
                                                            // The dirtyRef-during-render change above stops the
                                                            // snapshot listener from clobbering the draft mid-edit.
                                                            value={
                                                                row.score ?? ""
                                                            }
                                                            min={SCORE_MIN}
                                                            max={SCORE_MAX}
                                                            step={0.5}
                                                            allowNegative={false}
                                                            clampBehavior="none"
                                                            onChange={(v) => {
                                                                if (
                                                                    v === "" ||
                                                                    v == null
                                                                ) {
                                                                    updateDraftField(
                                                                        row.id,
                                                                        {
                                                                            score: undefined,
                                                                        }
                                                                    );
                                                                    return;
                                                                }
                                                                const n =
                                                                    typeof v ===
                                                                    "number"
                                                                        ? v
                                                                        : Number(
                                                                              v
                                                                          );
                                                                if (
                                                                    Number.isFinite(
                                                                        n
                                                                    )
                                                                )
                                                                    updateDraftField(
                                                                        row.id,
                                                                        { score: n }
                                                                    );
                                                            }}
                                                        />
                                                        {isUpdate &&
                                                            normalizeScore(
                                                                serverRowMap.get(
                                                                    row.id
                                                                )?.score
                                                            ) !==
                                                                normalizeScore(
                                                                    row.score
                                                                ) && (
                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    was:{" "}
                                                                    {serverRowMap
                                                                        .get(
                                                                            row.id
                                                                        )
                                                                        ?.score ??
                                                                        "—"}
                                                                </Text>
                                                            )}
                                                    </Stack>
                                                )}
                                            </Table.Td>
                                        ) : null}
                                        {showScore && (
                                            <Table.Td>
                                                {rating ? (
                                                    <Badge
                                                        size="sm"
                                                        color={ratingColor}
                                                        variant="light"
                                                    >
                                                        {rating}
                                                    </Badge>
                                                ) : (
                                                    <Text size="xs" c="dimmed">
                                                        —
                                                    </Text>
                                                )}
                                            </Table.Td>
                                        )}
                                        {listMeta.showFatfFlag && (
                                            <Table.Td>
                                                <Stack gap={2}>
                                                    <Switch
                                                        size="xs"
                                                        checked={
                                                            !!row.flags?.fatfFlag
                                                        }
                                                        disabled={isDelete}
                                                        onChange={(e) =>
                                                            updateDraftField(
                                                                row.id,
                                                                {
                                                                    flags: {
                                                                        ...(row.flags ||
                                                                            {}),
                                                                        fatfFlag:
                                                                            e
                                                                                .currentTarget
                                                                                .checked,
                                                                    },
                                                                }
                                                            )
                                                        }
                                                    />
                                                    {isUpdate &&
                                                        !!serverRowMap.get(
                                                            row.id
                                                        )?.flags?.fatfFlag !==
                                                            !!row.flags
                                                                ?.fatfFlag && (
                                                            <Text
                                                                size="xs"
                                                                c="dimmed"
                                                            >
                                                                was:{" "}
                                                                {serverRowMap.get(
                                                                    row.id
                                                                )?.flags
                                                                    ?.fatfFlag
                                                                    ? "Yes"
                                                                    : "No"}
                                                            </Text>
                                                        )}
                                                </Stack>
                                            </Table.Td>
                                        )}
                                        {canDeleteRows && (
                                            <Table.Td>
                                                {isDelete ? (
                                                    <Tooltip label="Undo delete">
                                                        <ActionIcon
                                                            size="sm"
                                                            color="gray"
                                                            variant="subtle"
                                                            onClick={() =>
                                                                undoDelete(
                                                                    row.id
                                                                )
                                                            }
                                                        >
                                                            <IconRotate
                                                                size={14}
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip label="Delete row">
                                                        <ActionIcon
                                                            size="sm"
                                                            color="red"
                                                            variant="subtle"
                                                            onClick={() =>
                                                                handleDeleteRequest(
                                                                    row
                                                                )
                                                            }
                                                        >
                                                            <IconTrash
                                                                size={14}
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </Table.Td>
                                        )}
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
            )}

            <Divider my="sm" />

            <Group justify="space-between">
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">
                        {sortedFiltered.length} of{" "}
                        {draft.length - pendingDeletes.size} item
                        {draft.length - pendingDeletes.size === 1 ? "" : "s"}
                        {search ? " (filtered)" : ""}
                        {pendingCount > 0
                            ? ` · ${pendingCount} unsaved`
                            : ""}
                    </Text>
                </Stack>
                <Group gap="xs">
                    {pendingCount > 0 && (
                        <Button
                            variant="default"
                            size="sm"
                            onClick={discardChanges}
                            disabled={saving || !!previewJobId}
                        >
                            Discard
                        </Button>
                    )}
                    <Button
                        size="sm"
                        leftSection={<IconDeviceFloppy size={16} />}
                        onClick={saveAllChanges}
                        loading={saving}
                        disabled={pendingCount === 0 || !!previewJobId}
                    >
                        Save changes
                    </Button>
                </Group>
            </Group>

            {canAdd && (
                <CramAddItemModal
                    opened={addOpen}
                    listMeta={listMeta}
                    sectorOptions={sectorOptions}
                    existingIds={
                        new Set([
                            ...draft.map((r) => r.id),
                            ...serverItems.map((r) => r.id),
                        ])
                    }
                    onClose={() => setAddOpen(false)}
                    onAdd={(item) => addNewItem(item)}
                />
            )}

            <ConfirmationModal
                opened={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={performDelete}
                title="Delete row?"
                message={
                    deleteTarget
                        ? `Mark "${deleteTarget.label}" for deletion? The row will stay visible until you click Save changes; nothing is removed from the database yet.`
                        : ""
                }
                confirmText="Mark for deletion"
                type="danger"
            />

            <CramRecalcPreviewModal
                jobId={previewJobId}
                listLabel={listMeta.labelEn}
                onClose={() => setPreviewJobId(null)}
                onApplied={handleApplied}
            />
        </Card>
    );
};

export default CramListEditor;
